import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { storyAPI } from '../shared/services/api';
import { useAuth } from '../features/auth/contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import AISuggestionModal from '../components/AISuggestionModal';
import { aiAPI } from '../services/aiAPI';
import { sanitizeStoryContent, setSecureStoryHTML, sanitizeUserInput } from '../utils/security';
import { settingsService, ImageSettings } from '../services/settingsService';
import { AiAnalysis } from '../shared/types';

// Helper function to format violation types for display
const formatViolations = (violations: string[]): string => {
  if (!violations || violations.length === 0) return '';

  const violationMap: Record<string, string> = {
    'VIOLENCE': 'violence',
    'ADULT_THEMES': 'adult themes',
    'INAPPROPRIATE_LANGUAGE': 'inappropriate language',
    'BULLYING': 'bullying/discrimination',
    'SCARY_CONTENT': 'scary content',
    'DISCRIMINATION': 'discrimination',
    'AGE_INAPPROPRIATE': 'age-inappropriate content'
  };

  const formattedViolations = violations
    .map(v => violationMap[v] || v.toLowerCase().replace(/_/g, ' '))
    .join(', ');

  return formattedViolations;
};

const EditorContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: calc(100vh - 160px);

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const EditorCard = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
`;

const EditorHeader = styled.div`
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  border-bottom: 1px solid var(--color-gray-200);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const EditorTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StoryMetaForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: end;
  width: 100%;

  > div:first-child {
    flex: 2;
    min-width: 200px;
  }

  > div:nth-child(2),
  > div:nth-child(3) {
    flex: 1;
    min-width: 150px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    
    > div:first-child,
    > div:nth-child(2),
    > div:nth-child(3) {
      flex: none;
      min-width: auto;
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--color-gray-700);
  font-family: var(--font-sans);
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  transition: border-color 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1.1rem;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  background: var(--color-white);
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1.1rem;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1.1rem;
    min-height: 100px;
  }
`;

const BookContainer = styled.div`
  display: flex;
  background: #f5f5f5;
  height: 600px;
  position: relative;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: 800px;
  }
`;

const BookPage = styled.div`
  flex: 1;
  background: var(--color-white);
  position: relative;
  border: 1px solid var(--color-gray-300);
  
  &:first-child {
    border-right: none;
    box-shadow: inset -10px 0 20px rgba(0,0,0,0.05);
  }
  
  &:last-child {
    box-shadow: inset 10px 0 20px rgba(0,0,0,0.05);
  }
  
  @media (max-width: 768px) {
    &:first-child {
      border-right: 1px solid var(--color-gray-300);
      border-bottom: none;
      box-shadow: inset 0 -10px 20px rgba(0,0,0,0.05);
    }
    
    &:last-child {
      box-shadow: inset 0 10px 20px rgba(0,0,0,0.05);
    }
  }
`;

const StyledSunEditor = styled.div`
  height: 100%;
  max-height: 600px;
  overflow: hidden;
  
  .sun-editor {
    border: none;
    height: 600px !important;
    max-height: 600px;
    
    .se-wrapper {
      height: 600px !important;
      max-height: 600px;
    }
    
    .se-wrapper-inner {
      height: 600px !important;
      max-height: 600px;
    }
    
    .se-container {
      height: 600px !important;
      max-height: 600px;
    }
    
    .se-contents {
      font-family: var(--font-serif);
      font-size: 1.1rem;
      line-height: 1.6;
      height: calc(600px - 42px) !important;
      max-height: calc(600px - 42px);
      padding: 2rem;
      background-image: 
        radial-gradient(circle at 1px 1px, rgba(0,0,0,.05) 1px, transparent 0);
      background-size: 20px 20px;
      background-color: #fefefe;
      overflow-y: auto;
      
      /* Support for image float and text wrapping - VERY SPECIFIC SELECTORS */
      .se-component.se-image-container {
        margin: 0.5rem 0 !important;
        display: inline-block !important;
        width: auto !important;
        max-width: none !important;
        clear: none !important;
        
        /* Handle left float */
        &.__se__float-left,
        &[data-align="left"] {
          float: left !important;
          margin: 0.5rem 1rem 0.5rem 0 !important;
          clear: none !important;
        }
        
        /* Handle right float */
        &.__se__float-right,
        &[data-align="right"] {
          float: right !important;
          margin: 0.5rem 0 0.5rem 1rem !important;
          clear: none !important;
        }
        
        /* Ensure figure maintains proper styling */
        figure {
          margin: 0 !important;
          display: block !important;
          width: auto !important;
          float: none !important;
        }
      }
      
      /* Override any clearing on elements that should wrap around images */
      .se-contents p,
      .se-contents div:not(.se-component),
      .se-contents h1,
      .se-contents h2, 
      .se-contents h3,
      .se-contents h4,
      .se-contents h5,
      .se-contents h6 {
        clear: none !important;
        overflow: visible !important;
      }
      
      /* Ensure paragraphs can wrap around floated images */
      p {
        margin: 0.5rem 0;
        clear: none !important;
        display: block;
        overflow: visible;
      }
      
      /* Ensure proper text flow around floated elements */
      div, p, h1, h2, h3, h4, h5, h6 {
        clear: none !important;
      }
      
      /* Clear floats after content blocks when needed */
      &::after {
        content: "";
        display: table;
        clear: both;
      }
    }
    
    .se-show-blocks {
      color: var(--color-gray-800);
    }
    
    .se-toolbar {
      border: none;
      border-bottom: 1px solid var(--color-gray-300);
      background: var(--color-gray-50);
      padding: 0.75rem 2rem;
    }

    /* Custom AI Button Styling */
    .custom-ai-btn {
      background: linear-gradient(135deg, #7B68EE 0%, #9370DB 100%) !important;
      color: white !important;
      border-radius: 6px !important;
      border: none !important;
      font-weight: bold !important;
      transition: all 0.2s ease !important;
    }

    /* Fallback floating AI button */
    .floating-ai-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: linear-gradient(135deg, #7B68EE 0%, #9370DB 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 8px !important;
      padding: 8px 12px !important;
      font-size: 13px !important;
      font-weight: bold !important;
      cursor: pointer !important;
      box-shadow: 0 2px 8px rgba(123, 104, 238, 0.3) !important;
      transition: all 0.2s ease !important;
      
      &:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 4px 16px rgba(123, 104, 238, 0.4) !important;
        background: linear-gradient(135deg, #8A2BE2 0%, #9932CC 100%) !important;
      }
    }
  }
`;


const SummarySection = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
`;

const SummaryTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 1rem;
`;

const CharacterCount = styled.div<{ isOverLimit: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.isOverLimit ? 'var(--color-error)' : 'var(--color-gray-600)'};
  text-align: right;
  margin-top: 0.25rem;
  font-weight: ${props => props.isOverLimit ? '600' : '400'};
`;

const StatusBanner = styled.div<{ status: 'rejected' | 'pending' }>`
  padding: 1rem 2rem;
  background: ${props => props.status === 'rejected'
    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'};
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  h4 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    margin: 0;
    opacity: 0.95;
    line-height: 1.5;
  }

  .violations {
    margin-top: 0.5rem;
    font-weight: 500;
    font-size: 0.95rem;
  }
`;

const SuggestButton = styled.button`
  background: var(--color-secondary);
  color: var(--color-gray-800);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  margin-left: 1rem;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #FBF0B3;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface StoryFormData {
  title: string;
  genre: string;
  visibility: string;
  summary: string;
}


// Helper function to find text using context and create range
const findTextWithContext = (container: Element, targetText: string, contextBefore: string, contextAfter: string): Range | null => {
  const fullText = container.textContent || '';
  const searchText = contextBefore + targetText + contextAfter;
  
  console.log('Searching for text:', { targetText: targetText.substring(0, 30), contextBefore, contextAfter });
  
  // Try to find the exact context match
  let searchStart = fullText.indexOf(searchText);
  if (searchStart === -1) {
    // Fallback: search for just the target text
    console.log('Context search failed, trying target text only');
    searchStart = fullText.indexOf(targetText);
    if (searchStart === -1) {
      console.log('Text not found in editor');
      return null;
    }
  } else {
    // Adjust to point to the target text within the context
    searchStart += contextBefore.length;
  }
  
  console.log('Found text at position:', searchStart);
  
  // Create range using TreeWalker
  const range = document.createRange();
  let currentOffset = 0;
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT
  );

  let startNode: Node | null = null;
  let startNodeOffset = 0;
  let endNode: Node | null = null;
  let endNodeOffset = 0;

  const targetStart = searchStart;
  const targetEnd = searchStart + targetText.length;

  let currentNode;
  while (currentNode = walker.nextNode()) {
    const nodeLength = currentNode.textContent?.length || 0;
    
    if (!startNode && currentOffset + nodeLength > targetStart) {
      startNode = currentNode;
      startNodeOffset = targetStart - currentOffset;
    }
    
    if (startNode && !endNode && currentOffset + nodeLength >= targetEnd) {
      endNode = currentNode;
      endNodeOffset = targetEnd - currentOffset;
      break;
    }
    
    currentOffset += nodeLength;
  }

  if (startNode && endNode) {
    try {
      range.setStart(startNode, Math.max(0, startNodeOffset));
      range.setEnd(endNode, Math.max(0, endNodeOffset));
      console.log('Successfully created range');
      return range;
    } catch (error) {
      console.error('Error creating range:', error);
      return null;
    }
  }
  
  console.log('Could not find start/end nodes');
  return null;
};

// Static image file type configuration
const STATIC_IMAGE_CONFIG = {
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'],
  ALLOWED_EXTENSIONS: '.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg'
};

const StoryEditor: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, login, user, syncUser } = useAuth();
  const isEditing = Boolean(id);
  
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [currentStory, setCurrentStory] = useState<any>(null);
  const [imageSettings, setImageSettings] = useState<ImageSettings>({
    maxImageSizeKb: 450, // Default 450KB as requested
    defaultMaxImageSizeKb: 450, // Default 450KB as requested
    maxImagesPerStory: 5, // Default 5 images for free users
    enabled: true
  });
  const [genres, setGenres] = useState<string[]>([]);

  // AI Suggestion Modal state
  const [selectedText, setSelectedText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const editorRef = useRef<any>(null);
  const lastSelectionRef = useRef<{ 
    text: string; 
    contextBefore: string;
    contextAfter: string;
  }>({ text: '', contextBefore: '', contextAfter: '' });
  
  const { register, handleSubmit, setValue, reset, watch } = useForm<StoryFormData>({
    defaultValues: {
      title: '',
      genre: 'Adventure',
      visibility: 'private',
      summary: ''
    }
  });

  // SunEditor doesn't use refs like ReactQuill

  useEffect(() => {
    if (isEditing) {
      loadStory().catch(console.error);
    }
  }, [id, isEditing]);

  // Load image settings from backend
  useEffect(() => {
    const loadImageSettings = async () => {
      try {
        const settings = await settingsService.getImageSettings();
        setImageSettings(settings);
      } catch (error) {
        console.error('Failed to load image settings:', error);
        // Keep default settings if loading fails
      }
    };

    loadImageSettings();
  }, []);

  // Load genres from backend
  useEffect(() => {
    const loadGenres = async () => {
      try {
        const response = await storyAPI.getGenres();
        setGenres(response.data);
      } catch (error) {
        console.error('Failed to load genres:', error);
        // Fallback to default genres if API fails
        setGenres(['Adventure', 'Fantasy', 'Mystery', 'Comedy', 'Drama', 'Educational', 'Fairy Tale', 'Science Fiction', 'Contemporary']);
      }
    };

    loadGenres();
  }, []);

  // Track text selection in SunEditor
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        lastSelectionRef.current = { text: '', contextBefore: '', contextAfter: '' };
        return;
      }

      const selectedTextContent = selection.toString().trim();
      const editorElement = document.querySelector('.se-contents');
      
      // Check if selection is within editor
      if (editorElement && editorElement.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        if (selectedTextContent.length >= 5 && selectedTextContent.length <= 500) {
          const fullText = editorElement.textContent || '';
          
          // Find the selected text in the full content
          const selectionStart = fullText.indexOf(selectedTextContent);
          if (selectionStart !== -1) {
            const contextBefore = fullText.substring(Math.max(0, selectionStart - 50), selectionStart);
            const contextAfter = fullText.substring(selectionStart + selectedTextContent.length, Math.min(fullText.length, selectionStart + selectedTextContent.length + 50));
            
            lastSelectionRef.current = {
              text: selectedTextContent,
              contextBefore,
              contextAfter
            };
            console.log('Text selection tracked:', {
              text: selectedTextContent.substring(0, 30) + '...',
              contextBefore: contextBefore.substring(Math.max(0, contextBefore.length - 20)),
              contextAfter: contextAfter.substring(0, 20)
            });
          }
        }
      }
    };

    const handleDoubleClick = (event: MouseEvent) => {
      // Check if double-click is within editor
      const editorElement = document.querySelector('.se-contents');
      if (!editorElement || !editorElement.contains(event.target as Node)) {
        return;
      }

      setTimeout(() => {
        if (lastSelectionRef.current.text) {
          const editorElement = document.querySelector('.se-contents');
          if (editorElement) {
            const range = findTextWithContext(
              editorElement, 
              lastSelectionRef.current.text,
              lastSelectionRef.current.contextBefore,
              lastSelectionRef.current.contextAfter
            );
            if (range) {
              setSelectedText(lastSelectionRef.current.text);
              setModalVisible(true);
              
              toast.success('💡 AI suggestions ready! Or use the 🤖 AI button in the toolbar.');
            }
          }
        }
      }, 50);
    };

    // Add event listeners
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('dblclick', handleDoubleClick);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  const loadStory = async () => {
    if (!id) return;

    try {
      const response = await storyAPI.getStory(id);
      const story = response.data;

      setCurrentStory(story);

      reset({
        title: story.title,
        genre: story.genre,
        visibility: story.visibility.toLowerCase(),
        summary: story.summary || ''
      });

      setContent(story.content?.html || '');
    } catch (error: any) {
      console.error('Failed to load story:', error);
      toast.error('Failed to load story');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: StoryFormData) => {
    if (!data.title.trim()) {
      toast.error('Please enter a story title');
      return;
    }

    // Validate summary length before submitting
    if (data.summary && data.summary.length > 1500) {
      toast.error('Your story summary is too long. Please make it shorter (maximum 1500 characters).');
      toast(`Current length: ${data.summary.length} characters`, { icon: 'ℹ️' });
      return;
    }

    setSaving(true);

    try {
      const storyData = {
        title: data.title,
        summary: data.summary,
        content: { html: content },
        genre: data.genre,
        visibility: data.visibility.toUpperCase()
      };

      if (isEditing) {
        // Get current story to check previous status
        const currentStoryResponse = await storyAPI.getStory(id!);
        const previousStatus = currentStoryResponse.data.publicationStatus;

        const response = await storyAPI.updateStory(id!, storyData as any);
        const updatedStory = response.data;

        // Check if story was flagged by AI moderation
        const requestedVisibility = data.visibility.toUpperCase();
        const actualVisibility = updatedStory.visibility;
        const currentStatus = updatedStory.publicationStatus;
        const wasMarkedForReview = currentStatus === 'ADMIN_REVIEW_PENDING';
        const wasRejected = currentStatus === 'REJECTED';
        const wasPreviouslyRejected = previousStatus === 'REJECTED' || previousStatus === 'ADMIN_REVIEW_PENDING';
        const nowDraft = currentStatus === 'DRAFT';

        // Check if story was previously rejected but now passed review
        if (wasPreviouslyRejected && nowDraft) {
          setValue('visibility', updatedStory.visibility.toLowerCase());
          toast.success('✅ Great! Your story passed AI review and is now ready to publish!', { duration: 10000 });
          toast('You can now set visibility to PUBLIC or FRIENDS.', { icon: '🎉', duration: 10000 });
        } else if (wasMarkedForReview || wasRejected) {
          // Story was flagged as inappropriate by AI moderation
          setValue('visibility', updatedStory.visibility.toLowerCase());

          // Format specific violations if available
          const violations = updatedStory.aiAnalysis?.violations || [];
          const violationText = violations.length > 0
            ? `: ${formatViolations(violations)}`
            : '';

          if (wasRejected) {
            toast.error(`Your story has been rejected due to inappropriate content${violationText}.`, { duration: 10000 });
            toast('Please revise your content to make it appropriate for children before resubmitting.', { icon: 'ℹ️', duration: 10000 });
          } else {
            toast.error(`Your story has been flagged for inappropriate content${violationText} and submitted for admin review.`, { duration: 10000 });
            toast('Please revise your content and try publishing again once it\'s approved.', { icon: 'ℹ️', duration: 10000 });
          }
        } else if ((requestedVisibility === 'PUBLIC' || requestedVisibility === 'FRIENDS') && actualVisibility === 'PRIVATE') {
          // Story was automatically made private due to inappropriate content (legacy fallback)
          setValue('visibility', 'private');
          toast.error('Your story was automatically made private due to inappropriate content. It has been submitted for admin review.', { duration: 10000 });
          toast('Please revise your content and try publishing again once it\'s approved.', { icon: 'ℹ️', duration: 10000 });
        } else {
          // Update form with any other changes from backend
          setValue('visibility', updatedStory.visibility.toLowerCase());
          toast.success('Story updated successfully!');
        }
      } else {
        // For now, let's just save locally if there are any issues
        try {
          const response = await storyAPI.createStory(storyData);
          const createdStory = response.data;
          
          // Check if new story was flagged as inappropriate
          const requestedVisibility = data.visibility.toUpperCase();
          const actualVisibility = createdStory.visibility;
          const wasMarkedForReview = createdStory.publicationStatus === 'ADMIN_REVIEW_PENDING';
          const wasRejected = createdStory.publicationStatus === 'REJECTED';

          if (wasMarkedForReview || wasRejected) {
            // Story was flagged as inappropriate by AI moderation
            // Format specific violations if available
            const violations = createdStory.aiAnalysis?.violations || [];
            const violationText = violations.length > 0
              ? `: ${formatViolations(violations)}`
              : '';

            if (wasRejected) {
              toast.error(`Your story has been rejected due to inappropriate content${violationText}.`, { duration: 10000 });
              toast('Please revise your content to make it appropriate for children before resubmitting.', { icon: 'ℹ️', duration: 10000 });
            } else {
              toast.error(`Your story has been flagged for inappropriate content${violationText} and submitted for admin review.`, { duration: 10000 });
              toast('Please revise your content and try publishing again once it\'s approved.', { icon: 'ℹ️', duration: 10000 });
            }
          } else if ((requestedVisibility === 'PUBLIC' || requestedVisibility === 'FRIENDS') && actualVisibility === 'PRIVATE') {
            toast.error('Your story was automatically made private due to inappropriate content. It has been submitted for admin review.', { duration: 10000 });
            toast('Please revise your content and try publishing again once it\'s approved.', { icon: 'ℹ️', duration: 10000 });
          } else {
            toast.success('Story created successfully!');
          }
          
          navigate(`/story/edit/${createdStory.id}`);
        } catch (authError: any) {
          console.error('Story creation error:', authError);
          
          // Always save locally as backup
          localStorage.setItem('draft_story', JSON.stringify({
            ...storyData,
            timestamp: new Date().toISOString()
          }));
          
          if (authError.response?.status === 401) {
            toast.error('Please log in to save your story permanently.');
            toast.success('Story draft saved locally as backup.');
          } else if (authError.response?.status === 400 && 
                     authError.response?.data?.message?.includes('User ID not available')) {
            toast.error('User not synced. Please click the "🔄 Sync User" button and try again.');
            toast.success('Story draft saved locally as backup.');
          } else {
            toast.error(`Failed to save story: ${authError.response?.data?.message || authError.message}`);
            toast.success('Story draft saved locally as backup.');
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to save story:', error);

      // Check if this is a validation error from inappropriate content
      if (error.response?.status === 400 &&
          error.response?.data?.fieldErrors?.visibility) {
        // Story was blocked from being made public due to inappropriate content
        setValue('visibility', 'private');
        toast.error('Your story cannot be made public due to inappropriate content.');
        toast('It has been submitted for admin review. Please revise your content and try again once approved.', { icon: 'ℹ️' });
      } else if (error.response?.status === 400 &&
                 error.response?.data?.fieldErrors?.summary) {
        // Summary is too long
        const summaryError = error.response.data.fieldErrors.summary;
        toast.error('Your story summary is too long. Please make it shorter (maximum 1500 characters).');
        toast(`Current length: ${data.summary?.length || 0} characters`, { icon: 'ℹ️' });
      } else if (error.response?.data?.message?.toLowerCase().includes('summary') &&
                 error.response?.data?.message?.toLowerCase().includes('long')) {
        // Alternative check for summary length error
        toast.error('Your story summary is too long. Please make it shorter (maximum 1500 characters).');
        toast(`Current length: ${data.summary?.length || 0} characters`, { icon: 'ℹ️' });
      } else {
        toast.error(`Failed to save story: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAISuggest = async () => {
    const currentContent = content;
    if (!currentContent.trim()) {
      toast.error('Please write some story content first');
      return;
    }

    if (currentContent.length < 50) {
      toast.error('Please write more content before generating a summary');
      return;
    }

    setGeneratingSummary(true);

    try {
      // Extract text content from HTML safely
      const tempDiv = document.createElement('div');
      setSecureStoryHTML(tempDiv, currentContent);
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      console.log('Generating summary for content:', textContent.substring(0, 100) + '...');
      
      const response = await aiAPI.summarizeStory(textContent);
      
      if (response.success) {
        // Post-process the summary to ensure proper word limit and complete sentences
        let summary = response.content.trim();
        
        // Split into words and check if we need to truncate
        const words = summary.split(/\s+/);
        if (words.length > 200) {
          // Truncate to 200 words and find the last complete sentence
          const truncatedText = words.slice(0, 200).join(' ');
          const sentences = truncatedText.split(/[.!?]+/);
          
          if (sentences.length > 1) {
            // Remove the last potentially incomplete sentence
            sentences.pop();
            summary = sentences.join('. ').trim();
            if (!summary.endsWith('.') && !summary.endsWith('!') && !summary.endsWith('?')) {
              summary += '.';
            }
          } else {
            summary = truncatedText + '...';
          }
        }
        
        setValue('summary', summary);
        toast.success('AI summary generated successfully!');
      } else {
        toast.error('Failed to generate summary: ' + response.message);
      }
    } catch (error: any) {
      console.error('Failed to generate summary:', error);
      toast.error('Failed to generate AI summary: ' + (error?.message || 'Unknown error'));
    } finally {
      setGeneratingSummary(false);
    }
  };

  // Handle accepting AI suggestion
  const handleAcceptSuggestion = (suggestion: string) => {
    console.log('Accepting suggestion:', suggestion.substring(0, 50) + '...');
    
    try {
      // Try multiple editor selectors
      const editorSelectors = [
        '.se-contents',
        '.sun-editor-editable',
        '.se-wrapper-inner .se-wrapper-wysiwyg .se-contents',
        '[contenteditable="true"]',
        '.note-editable'
      ];
      
      let editorElement = null;
      for (const selector of editorSelectors) {
        editorElement = document.querySelector(selector);
        if (editorElement) {
          console.log(`Found editor with selector: ${selector}`);
          break;
        }
      }

      if (!editorElement) {
        console.error('Editor element not found. Available elements:', {
          seContents: document.querySelector('.se-contents'),
          contentEditable: document.querySelector('[contenteditable="true"]'),
          sunEditor: document.querySelector('.sun-editor'),
        });
        toast.error('Editor not found');
        return;
      }

      // Get current content safely
      let currentContent = editorElement.innerHTML;
      console.log('Current content length:', currentContent.length);
      console.log('Selected text:', selectedText.substring(0, 100));
      
      // Simple text replacement - find and replace the selected text
      if (currentContent.includes(selectedText)) {
        const newContent = currentContent.replace(selectedText, sanitizeStoryContent(suggestion));
        setSecureStoryHTML(editorElement as HTMLElement, newContent);
        setContent(newContent);
        console.log('Text successfully replaced using innerHTML');
        toast.success('AI suggestion applied successfully!');
      } else {
        // Fallback: try to find the text in plain text content
        const textContent = editorElement.textContent || '';
        console.log('Fallback - text content length:', textContent.length);
        
        if (textContent.includes(selectedText)) {
          // Replace in plain text and wrap in paragraph
          const newTextContent = textContent.replace(selectedText, sanitizeUserInput(suggestion, 5000));
          const newHTML = `<p>${newTextContent.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
          setSecureStoryHTML(editorElement as HTMLElement, newHTML);
          setContent(newHTML);
          console.log('Text successfully replaced (fallback method)');
          toast.success('AI suggestion applied successfully!');
        } else {
          console.error('Selected text not found in content');
          toast.error('Selected text has changed and cannot be replaced. Please select the text again.');
          return;
        }
      }
      
    } catch (error: any) {
      console.error('Failed to replace text:', error);
      toast.error('Failed to apply suggestion: ' + (error?.message || 'Unknown error'));
    }
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedText('');
    
    // Clear selection
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  };

  // Handle AI suggestion button click
  const handleAIButtonClick = () => {
    console.log('AI Button clicked, checking selection...');
    
    // First try current selection
    const selection = window.getSelection();
    let currentText = '';

    if (selection && selection.rangeCount > 0) {
      currentText = selection.toString().trim();
      const editorElement = document.querySelector('.se-contents');
      if (editorElement && editorElement.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        console.log('Found current selection:', currentText.substring(0, 50) + '...');
      }
    }

    // Fall back to last tracked selection if current is invalid
    if (!currentText || currentText.length < 5 || currentText.length > 500) {
      if (lastSelectionRef.current.text) {
        currentText = lastSelectionRef.current.text;
        console.log('Using tracked selection:', currentText.substring(0, 50) + '...');
      }
    }

    // Validate final selection
    if (!currentText || currentText.length < 5 || currentText.length > 500) {
      toast.error('Please select between 5-500 characters in the story editor first');
      return;
    }

    console.log('Triggering AI modal for:', currentText.substring(0, 50) + '...');
    setSelectedText(currentText);
    setModalVisible(true);
  };

  // Add custom AI button to toolbar after editor initializes
  const addAIButton = (sunEditor: any) => {
    if (!sunEditor) {
      console.log('AI Button: No sunEditor instance');
      return;
    }
    
    const tryAddButton = (attempt: number = 1) => {
      console.log(`AI Button: Attempt ${attempt} to add button`);
      
      // Try multiple selectors to find the toolbar
      const selectors = [
        '.se-toolbar',
        '.se-toolbar .se-btn-list',
        '.sun-editor .se-toolbar',
        '.se-wrapper .se-toolbar'
      ];
      
      let toolbar: Element | null = null;
      for (const selector of selectors) {
        toolbar = document.querySelector(selector);
        if (toolbar) {
          console.log(`AI Button: Found toolbar with selector: ${selector}`);
          break;
        }
      }

      if (!toolbar) {
        console.log('AI Button: No toolbar found');
        if (attempt < 5) {
          setTimeout(() => tryAddButton(attempt + 1), 1000);
        } else {
          // Fallback: Create floating button
          console.log('AI Button: Creating fallback floating button');
          createFloatingAIButton();
        }
        return;
      }

      // Check if button already exists
      if (toolbar.querySelector('.custom-ai-btn')) {
        console.log('AI Button: Button already exists');
        return;
      }

      // Create separator if needed
      const separator = document.createElement('div');
      separator.className = 'se-toolbar-separator-vertical';
      separator.style.cssText = 'margin: 0 3px; height: 100%; border-left: 1px solid #dadada;';

      // Create AI button
      const aiButton = document.createElement('button');
      aiButton.type = 'button';
      aiButton.className = 'se-btn se-tooltip custom-ai-btn';
      aiButton.textContent = '🤖 AI';
      aiButton.title = 'AI Suggestions - Select text and click for AI improvements';
      aiButton.style.cssText = `
        background: white !important;
        border: 2px solid #7B68EE !important;
        border-radius: 6px !important;
        margin: 0 4px !important;
        padding: 4px 8px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        color: #7B68EE !important;
        cursor: pointer !important;
        transition: all 0.2s ease !important;
        min-width: 60px !important;
        height: 28px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-shadow: 0 1px 3px rgba(123, 104, 238, 0.1) !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        line-height: 1 !important;
      `;

      aiButton.addEventListener('mouseenter', () => {
        aiButton.style.transform = 'translateY(-1px)';
        aiButton.style.boxShadow = '0 3px 8px rgba(123, 104, 238, 0.3)';
        aiButton.style.background = 'linear-gradient(135deg, #7B68EE 0%, #9370DB 100%)';
        aiButton.style.color = 'white';
      });

      aiButton.addEventListener('mouseleave', () => {
        aiButton.style.transform = 'translateY(0)';
        aiButton.style.boxShadow = '0 1px 3px rgba(123, 104, 238, 0.1)';
        aiButton.style.background = 'white';
        aiButton.style.color = '#7B68EE';
      });

      aiButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('AI Button clicked!');
        handleAIButtonClick();
      });
      
      toolbar.appendChild(separator);
      toolbar.appendChild(aiButton);
      console.log('AI Button: Successfully added to toolbar');
    };

    // Start trying to add the button
    setTimeout(() => tryAddButton(), 100);
  };

  // Create floating AI button as fallback
  const createFloatingAIButton = () => {
    // Check if floating button already exists
    if (document.querySelector('.floating-ai-btn')) return;

    const editorContainer = document.querySelector('.se-wrapper') || document.querySelector('.sun-editor');
    if (!editorContainer) return;

    const floatingButton = document.createElement('button');
    floatingButton.type = 'button';
    floatingButton.className = 'floating-ai-btn';
    floatingButton.textContent = '🤖 AI';
    floatingButton.title = 'AI Suggestions - Select text and click for AI improvements';
    
    floatingButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Floating AI Button clicked!');
      handleAIButtonClick();
    });

    // Position relative to editor container
    (editorContainer.parentNode as HTMLElement).style.position = 'relative';
    editorContainer.parentNode?.appendChild(floatingButton);
    
    console.log('AI Button: Fallback floating button created');
  };

  const sunEditorOptions = React.useMemo(() => ({
    buttonList: [
      ['undo', 'redo'],
      ['font', 'fontSize', 'formatBlock'],
      ['paragraphStyle'],
      ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
      ['fontColor', 'hiliteColor'],
      ['removeFormat'],
      ['outdent', 'indent'],
      ['align', 'horizontalRule', 'list', 'lineHeight'],
      ['table', 'link', 'image'],
      ['fullScreen', 'showBlocks', 'codeView']
    ],
    height: '600px',
    width: '100%',
    minHeight: '600px',
    maxHeight: '600px',
    placeholder: `Start writing your story here... 
💡 Tips: 
• Select text and click the 🤖 AI button for AI suggestions!
• Images: Maximum ${Math.round(imageSettings.defaultMaxImageSizeKb / 1024 * 100) / 100}MB, up to ${imageSettings.maxImagesPerStory} per story, supports JPG, PNG, GIF, WebP, BMP, SVG`,
    showPathLabel: false,
    charCounter: true,
    maxCharCount: 50000,
    resizingBar: false,
    // Image upload restrictions
    imageUploadSizeLimit: imageSettings.defaultMaxImageSizeKb * 1024, // Convert KB to bytes
    imageAccept: STATIC_IMAGE_CONFIG.ALLOWED_EXTENSIONS,
    imageUploadUrl: undefined, // Disable server upload, use base64 only
    imageFileInput: true,
    imageUrlInput: true,
    imageUploadHeader: undefined,
    imageMultipleFileInput: false,
    // Custom image upload handler with size validation
    onImageUploadBefore: function(files: FileList, info: any, uploadHandler: any): boolean {
      console.log('Image upload attempted:', files);
      
      if (!imageSettings.enabled) {
        toast.error('Image uploads are currently disabled.');
        return false;
      }
      
      const maxSizeBytes = imageSettings.defaultMaxImageSizeKb * 1024;
      const maxSizeMB = Math.round(imageSettings.defaultMaxImageSizeKb / 1024 * 100) / 100;
      
      // Check each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size
        if (file.size > maxSizeBytes) {
          toast.error(`Image "${file.name}" is too large. Maximum size is ${maxSizeMB}MB. Current size: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
          return false; // Prevent upload
        }
        
        // Check file type
        if (!STATIC_IMAGE_CONFIG.ALLOWED_TYPES.includes(file.type)) {
          toast.error(`Invalid file type "${file.type}". Allowed types: JPG, PNG, GIF, WebP, BMP, SVG`);
          return false; // Prevent upload
        }
      }
      
      // All files passed validation, proceed with upload
      return true;
    },
    // Handle successful image insertion
    onImageUpload: function(targetElement: HTMLImageElement, index: number, state: string, imageInfo: any, remainingFilesCount: number): void {
      console.log('Image uploaded successfully:', imageInfo);
      
      // Add some styling to maintain aspect ratio and limit size
      targetElement.style.maxWidth = '100%';
      targetElement.style.height = 'auto';
      targetElement.style.display = 'block';
      targetElement.style.margin = '10px auto';
      
      toast.success('Image added successfully!');
    },
    // Handle upload errors
    onImageUploadError: function(errorMessage: string, result: any): void {
      console.error('Image upload error:', errorMessage, result);
      toast.error(`Failed to upload image: ${errorMessage}`);
    }
  }), [imageSettings]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <EditorContainer>
      <EditorCard>
        <form onSubmit={handleSubmit(onSubmit)}>
          <EditorHeader>
            <HeaderControls>
              <div>
                <EditorTitle>
                  {isEditing ? t('editor.editStory') : t('editor.createNewStory')}
                </EditorTitle>
                {!isAuthenticated && (
                  <div style={{ 
                    color: 'var(--color-gray-600)', 
                    fontSize: '0.9rem', 
                    marginTop: '0.25rem' 
                  }}>
                    ⚠️ {t('editor.notLoggedInWarning')}
                  </div>
                )}
                {isAuthenticated && !user && (
                  <div style={{ 
                    color: 'var(--color-gray-600)', 
                    fontSize: '0.9rem', 
                    marginTop: '0.25rem' 
                  }}>
                    🔄 {t('editor.syncingUserData')}
                  </div>
                )}
              </div>
              <HeaderButtons>
                {!isAuthenticated && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={login}
                  >
                    🔐 {t('editor.loginToSave')}
                  </button>
                )}
                {isAuthenticated && !user && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={syncUser}
                  >
                    🔄 Sync User
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/dashboard')}
                >
                  {t('editor.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? t('editor.saving') : (isAuthenticated && user) ? t('editor.saveStory') : t('editor.saveDraft')}
                </button>
              </HeaderButtons>
            </HeaderControls>

            <StoryMetaForm>
              <FormGroup>
                <Label>{t('editor.storyTitle')}</Label>
                <Input
                  {...register('title', { required: true })}
                  placeholder={t('editor.titlePlaceholder')}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>{t('editor.genre')}</Label>
                <Select {...register('genre')}>
                  {genres.map(genre => (
                    <option key={genre} value={genre}>
                      {t(`genres.${genre}`, genre)}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>{t('editor.visibility')}</Label>
                <Select {...register('visibility')}>
                  <option value="private">{t('editor.visibilityPrivate')}</option>
                  <option value="friends">{t('editor.visibilityFriends')}</option>
                  <option value="public">{t('editor.visibilityPublic')}</option>
                </Select>
              </FormGroup>
            </StoryMetaForm>
          </EditorHeader>

          {/* Status Banner for Rejected/Pending Stories */}
          {currentStory && (currentStory.publicationStatus === 'REJECTED' || currentStory.publicationStatus === 'ADMIN_REVIEW_PENDING') && currentStory.aiAnalysis && (
            <StatusBanner status={currentStory.publicationStatus === 'REJECTED' ? 'rejected' : 'pending'}>
              <h4>
                {currentStory.publicationStatus === 'REJECTED' ? '⛔ Story Rejected' : '⚠️ Pending Admin Review'}
              </h4>
              <p>
                {currentStory.publicationStatus === 'REJECTED'
                  ? 'Your story has been automatically rejected due to inappropriate content. Please revise it to make it suitable for children aged 5-12.'
                  : 'Your story has been flagged for inappropriate content and is pending admin review. You can continue editing while awaiting approval.'}
              </p>
              {currentStory.aiAnalysis.violations && currentStory.aiAnalysis.violations.length > 0 && (
                <div className="violations">
                  Issues detected: {formatViolations(currentStory.aiAnalysis.violations)}
                </div>
              )}
            </StatusBanner>
          )}

          <BookContainer>
            <BookPage style={{ flex: 'none', width: '100%' }}>
              <StyledSunEditor>
                <SunEditor
                  defaultValue={content}
                  onChange={setContent}
                  setOptions={sunEditorOptions}
                  placeholder="Start writing your story here..."
                  getSunEditorInstance={(sunEditor) => {
                    editorRef.current = sunEditor;
                    addAIButton(sunEditor);
                  }}
                />
              </StyledSunEditor>
            </BookPage>
          </BookContainer>

          <SummarySection>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <SummaryTitle>{t('editor.storySummary')}</SummaryTitle>
              <SuggestButton type="button" onClick={handleAISuggest} disabled={generatingSummary}>
                {generatingSummary ? (
                  <>
                    <span style={{ 
                      display: 'inline-block', 
                      animation: 'spin 1s linear infinite',
                      marginRight: '0.5rem'
                    }}>⏳</span>
                    Generating...
                  </>
                ) : (
                  <>✨ AI Suggest</>
                )}
              </SuggestButton>
            </div>
            <TextArea
              {...register('summary')}
              placeholder={t('editor.summaryPlaceholder')}
              rows={3}
            />
            <CharacterCount isOverLimit={(watch('summary')?.length || 0) > 1500}>
              {watch('summary')?.length || 0} / 1500 characters
            </CharacterCount>
          </SummarySection>
        </form>
      </EditorCard>


      <AISuggestionModal
        selectedText={selectedText}
        visible={modalVisible}
        onClose={handleCloseModal}
        onAccept={handleAcceptSuggestion}
      />
    </EditorContainer>
  );
};

export default StoryEditor;